import { Component, OnInit, OnDestroy, EventEmitter, Input, Output } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { EtiquetadoContextService } from "../../../../context/etiquetado-context.service";
import { EtiquetadoService } from "../../../../services/etiquetado.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: 'app-inc-consumos-list',
    templateUrl: './consumos-list.component.html'
})
export class IncConsumosListComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();
    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersForm: UntypedFormGroup;
    public filtersVisible = true;

    public almacenes;
    public municipios;
    public etiquetadores;
    public confecciones;
    public campanyas;
    public denominaciones = [];

    public consumos = [];
    public selectedConsumos = new Array<{consumoId: number, snc: string}>();
    public paisId;

    public disabledAlmacenes: Subject<boolean> = new Subject();
    public disabledMunicipios: Subject<boolean> = new Subject();
    public disabledConfecciones: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();

    private toCloseSpinner = 0;

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    @Input() consumoId;
    @Input() consumoCampoId;

    @Output() consumosList = new EventEmitter<Array<any>>();
    @Output() multipleConsumos = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<Message>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _sessionContext: SessionContextService,
        private readonly _translate: TranslateService,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _etiquetadoContext: EtiquetadoContextService,
        public readonly _utilsService: UtilsService,
        private readonly _dataProvider: DataProvider
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{value: '', disabled: true}, Validators.required],
            campanya: [{value: '', disabled: true}, Validators.required],
            municipio: [{value: '', disabled: true}],
            finca: [''],
            productor: [''],
            fechaInicio: [''],
            fechaFin: [''],
            etiquetador: [{value: '', disabled: true}, Validators.required],
            almacen: [{value: '', disabled: true}],
            confeccion: [{value: '', disabled: true}],
            codigoSNC: ['']
        });

        this._etiquetadoContext.incidenciaInfoFilters = null;
        this._etiquetadoContext.consumosFilters = null;

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.denominaciones = [];
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
            }
        });

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
                this.getMunicipiosByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.disabledMunicipios.next(true);
                this.campanyas = [];
                this.municipios = [];
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('municipio').patchValue('');
                this.filtersForm.get('campanya').disable();
                this.filtersForm.get('municipio').disable();
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.getEtiquetadoresByCampanya(campanya.id, this.paisId);
            } else {
                this.disabledEtiquetadores.next(true);
                this.etiquetadores = [];
                this.filtersForm.get('etiquetador').patchValue('');
                this.filtersForm.get('etiquetador').disable();
            }
        });

        this.filtersForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getConfeccionesByEtiquetador(etiquetador.id);
                this.getAlmacenesByEtiquetador(etiquetador.id);
            } else {
                this.disabledConfecciones.next(true);
                this.disabledAlmacenes.next(true);
                this.confecciones = [];
                this.almacenes = [];
                this.filtersForm.get('confeccion').patchValue('');
                this.filtersForm.get('confeccion').disable();
                this.filtersForm.get('almacen').patchValue('');
                this.filtersForm.get('almacen').disable();
            }
        });

        if (this.consumoId) {
            this.getConsumo();
        } else {
            this.checkWarnings(this.filtersForm.get('pais').value);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get paises() {
        const paisesFiltrados = this.role === 'Administrador' ? this._dataProvider.paises : this._dataProvider.paises.filter(x => x.tienePDConGestionIncidencias);

        if (paisesFiltrados) {
            if (paisesFiltrados === 1 && this.filtersForm.get('pais').enabled) {
              this.filtersForm.get('pais').patchValue(paisesFiltrados[0]);
              this.filtersForm.get('pais').disable();
            }
          }
        return paisesFiltrados;
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            habilitada: true
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                if (this.campanyas.length === 1) {
                    this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
                } else {
                    this.filtersForm.get('campanya').enable();
                    this.disabledCampanyas.next(false);
                }
            }
        });
    }

    public getEtiquetadoresByCampanya(campanyaId, paisId) {
        let filters = {
            campanyaId: campanyaId,
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getEtiquetadores(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.etiquetadores = resp.lista;
                if (this.etiquetadores.length === 1) {
                    this.filtersForm.get('etiquetador').patchValue(this.etiquetadores[0]);
                    this.filtersForm.get('etiquetador').disable();
                    this.disabledEtiquetadores.next(true);
                } else {
                    this.filtersForm.get('etiquetador').enable();
                    this.disabledEtiquetadores.next(false);
                }
            }
        });
    }

    public getMunicipiosByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getMunicipiosByProvinciaId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.municipios = resp.lista;
                this.disabledMunicipios.next(false);
            }
        });
    }

    public getAlmacenesByEtiquetador(etiquetadorId) {
        let filters = {
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getAlmacenesByEtiquetadorId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.almacenes = resp.lista;
                this.disabledAlmacenes.next(false);
            }
        });
    }

    public getConfeccionesByEtiquetador(etiquetadorId) {
        let filters = {
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getConfecciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.confecciones = resp.lista;
                this.filtersForm.get('confeccion').enable();
                this.disabledConfecciones.next(false);
            }
        });
    }

    public get allChecked(): boolean {
        let isAllChecked = true;
        this.consumos.forEach(consumo => {
            if (!this.selectedConsumos.some(x => x.consumoId === consumo.id)) {
                isAllChecked = false;
            }
        });
        return isAllChecked;
    }

    public selectConsumo(consumo, value: boolean, all?: boolean) {
        if (value) {
            if (!this.selectedConsumos.some(x => x.consumoId === consumo.id)) {
                this.selectedConsumos.push({consumoId: consumo.id, snc: consumo.seriadoFijo});
            }
        } else {
            if (this.selectedConsumos.some(x => x.consumoId === consumo.id)) {
                this.selectedConsumos.splice(this.selectedConsumos.findIndex(x => x.consumoId === consumo.id), 1);
            }
        }

        if (!all) {
            this.multipleConsumos.emit(this.selectedConsumos.length > 1);
            this.consumosList.emit(this.selectedConsumos);
        }
    }

    public selectAll(value: boolean) {
        this.consumos.forEach(consumo => {
            this.selectConsumo(consumo, value, true);
        });
        this.multipleConsumos.emit(value);
        this.consumosList.emit(this.selectedConsumos);
    }

    private checkWarnings(campanya) {
        if (campanya && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getConsumos();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensaje.emit({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public pageChange(page) {
        this._etiquetadoContext.incidenciaInfoFilters.numPagina = this.currentPage = page - 1;
        this.getConsumos();
    }

    public itemsPerPageChange(items) {
        this._etiquetadoContext.incidenciaInfoFilters.numPagina = this.currentPage = 0;
        this._etiquetadoContext.incidenciaInfoFilters.tamPagina = this.itemsPerPage = items;
        this.getConsumos();
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (this.filtersForm.get('etiquetador').value ||
            this.filtersForm.get('almacen').value ||
            this.filtersForm.get('municipio').value ||
            this.filtersForm.get('finca').value ||
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled ) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('productor').value ||
            this.filtersForm.get('confeccion').value ||
            this.filtersForm.get('codigoSNC').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset({
            pais: this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').value : null,
            denominacion: this.filtersForm.get('denominacion').disabled ? this.filtersForm.get('denominacion').value : null
        });
    }

    public filtrar() {
        this.validDates();
        if (this.filtersForm.invalid) {
            this.showError = true;
            this.mensaje.emit({ class: 'error', message: 'global.error.required' });
        } else if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._etiquetadoContext.incidenciaInfoFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : null,
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : null,
            almacenId: this.filtersForm.get('almacen').value ? this.filtersForm.get('almacen').value.id : null,
            municipioId: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : null,
            codigoSNC: this.filtersForm.get('codigoSNC').value ? this.filtersForm.get('codigoSNC').value : '',
            confeccionId: this.filtersForm.get('confeccion').value ? this.filtersForm.get('confeccion').value.id : null,
            desFinca: this.filtersForm.get('finca').value ? this.filtersForm.get('finca').value : '',
            desProductor: this.filtersForm.get('productor').value ? this.filtersForm.get('productor').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage,
        };

        this.getConsumos();
    }

    public getConsumos() {
        if (this._etiquetadoContext.incidenciaInfoFilters) {
            this._filters = this._etiquetadoContext.incidenciaInfoFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._etiquetadoService.getConsumos(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.consumos = resp.lista;
        });
    }

    private getConsumo() {
        this._spinnerService.showSpinner();
        this._etiquetadoService.getConsumo(this.consumoId).subscribe((resp) => {
            this._spinnerService.hideSpinner();
            this.consumos.push(resp);
            this.consumosList.emit(resp.id);
        });
    }

    public getChecked(consumoId): boolean {
        return this.selectedConsumos.some(x => x.consumoId === consumoId)
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }
}
